import { Box, Card, Grid, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { parseISO } from 'date-fns';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { EnumProjectMilestoneStatus, mapCurrency } from 'tdc-web-backend/enums/enums';
import { useTranslation } from 'react-i18next';
import ControlledTextInput from '../../../../../../components/custom-inputs/ControlledTextInput';
import ControlledDateInput from '../../../../../../components/custom-inputs/ControlledDateInput';
import RecurringMonthlyBudgetDisplay from './RecurringMonthlyBudgetDisplay';
import BaseInputLabel from '../../../../../../components/custom-inputs/BaseInputLabel';
import ControlledContractMilestoneNumberInput from '../../../../../../components/custom-inputs/ControlledContractMilestoneNumberInput';
import ContractMilestoneBudgetProgress from './MilestoneBudgetProgress';
import StatusChipField from '../../../../../../components/StatusChipField/StatusChipField';
import CustomTooltip from '../../../../../../components/tooltip/CustomTooltip';

type MilestoneItemProps = {
  index: number;
  onRemove: (index: number) => void;
  disabled?: boolean;
  totalMilestones: number;
};

const MilestoneItem = ({
  index,
  onRemove,
  disabled = false,
  totalMilestones,
}: MilestoneItemProps) => {
  const { control, setValue } = useFormContext();
  const { t } = useTranslation();
  const currentStart = useWatch({
    control,
    name: `body.milestones.${index}.start`,
  });

  const currentEnd = useWatch({
    control,
    name: `body.milestones.${index}.end`,
  });

  const currency = useWatch({
    control,
    name: 'body.scope.currency',
  });

  const budget = useWatch({
    control,
    name: `body.milestones.${index}.budget`,
  });

  const duration = useWatch({
    control,
    name: `body.milestones.${index}.duration`,
  });

  const initialAmount = useWatch({
    control,
    name: 'initialAmount',
  });

  const status = useWatch({
    control,
    name: `body.milestones.${index}.status`,
  });

  const isEditable =
    !status ||
    status === EnumProjectMilestoneStatus.Pending ||
    status === EnumProjectMilestoneStatus.InProgress;

  const isDisabled = disabled || !isEditable;

  const totalProjectBudget = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: 'standard',
    maximumFractionDigits: 2,
  }).format(initialAmount);

  useEffect(() => {
    if (!currentStart || !currentEnd) return;

    const startDate = parseISO(currentStart);
    const endDate = parseISO(currentEnd);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;

    if (startDate > endDate) {
      setValue(`body.milestones.${index}.end`, '', { shouldValidate: true, shouldDirty: true });
    }
  }, [currentStart, currentEnd, index, setValue]);

  return (
    <Card
      elevation={0}
      sx={{
        position: 'relative',
        border: 'none',
        borderRadius: 0.5,
        boxShadow: '1px 10px 30px rgba(56, 69, 114, 0.1)',
        p: 3,
      }}
    >
      <Stack direction="row">
        <Box pr={1.5} pt={4}>
          <Typography variant="bodySemiBold2" color="primaryDark.500">
            {index < 9 ? `0${index + 1}` : index + 1}
          </Typography>
        </Box>
        <Box sx={{ width: '100%' }}>
          {!isDisabled && (index !== 0 || (index === 0 && totalMilestones > 1)) && (
            <IconButton
              sx={{
                position: 'absolute',
                right: 15,
                top: 11,
              }}
              onClick={() => onRemove(index)}
              disabled={index === 0 && totalMilestones === 1}
            >
              <CloseIcon />
            </IconButton>
          )}
          {status && (
            <Box>
              <StatusChipField
                text={status}
                sx={{
                  position: 'absolute',
                  right: 66,
                  top: 19,
                }}
              />
            </Box>
          )}
          <Stack pb={3} spacing={4}>
            <Stack spacing={2} direction="row" alignItems="baseline">
              <Stack
                width="50%"
                sx={{
                  paddingRight: 0.625,
                }}
              >
                <ControlledTextInput
                  name={`body.milestones.${index}.name`}
                  sx={{ bgcolor: 'common.white' }}
                  required
                  label={t('projects.milestoneName')}
                  disabled={isDisabled}
                />
              </Stack>

              <Stack spacing={2}>
                <BaseInputLabel required text="" />
              </Stack>
            </Stack>
            <Stack sx={{ alignItems: 'start' }}>
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="start"
                spacing={3}
              >
                <Grid item xs={6}>
                  <BaseInputLabel
                    required
                    text={t('contracts.milestoneDuration')}
                    sx={{ color: isDisabled ? 'primaryDark.300' : 'primaryDark.500' }}
                  />
                  <Stack
                    direction={{ md: 'column', lg: 'row' }}
                    sx={{
                      '.MuiBox-root': {
                        flex: 1,
                      },
                    }}
                    spacing={{ md: 0.5, lg: 0.5 }}
                  >
                    <Stack
                      direction={{ md: 'row', lg: 'row' }}
                      sx={{
                        flex: 1,
                      }}
                    >
                      <BaseInputLabel
                        sx={{
                          overflow: 'initial',
                          marginRight: 0.5,
                          mb: 0,
                          color: isDisabled ? 'primaryDark.300' : 'primaryDark.500',
                        }}
                        text={t('common.from')}
                      />
                      <ControlledDateInput
                        disabled={isDisabled}
                        components={{
                          OpenPickerIcon: KeyboardArrowDownIcon,
                        }}
                        slotProps={{
                          openPickerIcon: {
                            sx: {
                              color: isDisabled ? 'primaryDark.300' : 'primaryDark.500',
                            },
                          },
                        }}
                        name={`body.milestones.${index}.start`}
                        sx={{
                          flex: 1,
                        }}
                        inputProps={{ style: { fontSize: '0.83rem' } }}
                        required
                      />
                    </Stack>
                    <Stack
                      direction={{ md: 'row', lg: 'row' }}
                      sx={{
                        flex: 1,
                        color: isDisabled ? 'primaryDark.300' : 'primaryDark.500',
                      }}
                    >
                      <BaseInputLabel
                        sx={{
                          overflow: 'initial',
                          mx: { md: '0 !important', lg: '8px !important' },
                          whiteSpace: 'pre',
                          mb: 0,
                          pr: { md: 2.5, lg: 0 },
                          color: isDisabled ? 'primaryDark.300' : 'primaryDark.500',
                        }}
                        text={t('common.to')}
                      />

                      <ControlledDateInput
                        disabled={isDisabled}
                        components={{
                          OpenPickerIcon: KeyboardArrowDownIcon,
                        }}
                        slotProps={{
                          openPickerIcon: {
                            sx: {
                              color: isDisabled ? 'primaryDark.300' : 'primaryDark.500',
                            },
                          },
                        }}
                        required
                        fullWidth
                        defaultCalendarMonth={currentStart ? parseISO(currentStart) : null}
                        minDate={currentStart ? parseISO(currentStart) : null}
                        name={`body.milestones.${index}.end`}
                        sx={{
                          flex: 1,
                        }}
                        errorMessages={{
                          minDate: t('errors.endDateAfterStartDate'),
                        }}
                        inputProps={{ style: { fontSize: '0.83rem' } }}
                      />
                    </Stack>
                  </Stack>
                </Grid>

                <Grid
                  item
                  sx={{
                    paddingTop: 0,
                  }}
                  xs={6}
                >
                  <Stack spacing={2} direction={{ md: 'column', lg: 'row' }}>
                    <Stack direction="column">
                      <BaseInputLabel
                        required
                        text={t('contracts.milestoneBudget')}
                        sx={{ color: isDisabled ? 'primaryDark.300' : 'primaryDark.500' }}
                      />
                      <ControlledContractMilestoneNumberInput
                        disabled={isDisabled}
                        initialValue={budget}
                        name={`body.milestones.${index}.budget`}
                        adornmentText={mapCurrency.get(currency)}
                        adornmentSx={{
                          '.MuiTypography-root': {
                            color: isDisabled ? 'primaryDark.300' : 'primaryDark.600',
                          },
                        }}
                      />
                    </Stack>

                    <Stack
                      spacing={3}
                      direction={{ md: 'row', lg: 'column' }}
                      alignItems="center"
                      mt={{ md: '15px !important', lg: '0px !important' }}
                      sx={{
                        width: '100%',
                      }}
                    >
                      <CustomTooltip title={t('contracts.totalProjectBudget')}>
                        <BaseInputLabel text={`% ${t('common.of')} ${totalProjectBudget}`} />
                      </CustomTooltip>
                      <ContractMilestoneBudgetProgress index={index} />
                    </Stack>
                    {!!duration && <RecurringMonthlyBudgetDisplay index={index} />}
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
            <Stack>
              <ControlledTextInput
                label={t('projects.milestoneDescription')}
                name={`body.milestones.${index}.summary`}
                multiline
                sx={{ bgcolor: 'white' }}
                minRows={5}
                placeholder={t('common.type')}
                required
                variant="outlined"
                disabled={isDisabled}
              />
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
};

export default MilestoneItem;
